import { Injectable } from '@angular/core';
import { CompareHelper } from '@barthauer/abc/core';
import { ProfileService } from '@barthauer/abc/core/authentication';
import { AbcConfigService } from '@barthauer/abc/core/configuration';
import {
  CacheContext,
  CacheDataSet,
  CacheProgress,
  CacheService,
  DataService as CoreDataService,
  ModelBase,
  PersistObjectsModel,
  Query,
  QueryColumn,
  QueryObjectsModel,
  QueryOutputMode,
} from '@barthauer/abc/core/data';
import {
  DataEntity,
  QueryMapInfoModel,
  QueryMapObjectsModel,
  QueryMapObjectValuesModel,
} from '@barthauer/abc/graphics/adapters/open-layers';
import { forkJoin, from, Observable, of, Subject } from 'rxjs';
import { concatMap, map, tap } from 'rxjs/operators';
import { BasysMapFrameService } from '../basys-map-frame.service';
import { EntityGeometry, MapInfo, MapObjectValue } from '../models/mapsData.model';
import {
  MapInfoObjectTypeViewModel,
  MapInfoViewModel,
  ProfileInfoWithArenaViewModel,
} from '../viewModels/mapsData.viewModel';
import { ParameterValue } from '@barthauer/abc/queries/parameters/core';

@Injectable({
  providedIn: 'root',
})
export class DataService {
  public static readonly MainObjectSpaceName: string = 'BaSYS.Complete';
  private cacheDataSet: CacheDataSet;

  constructor(
    private coreDataService: CoreDataService,
    private config: AbcConfigService,
    private cache: CacheService,
    private profileService: ProfileService
  ) {}

  getMapInfo(packageId?: string, mapNames?: string[], additionalObjectTypeNames?: string[]): Observable<MapInfo> {
    const query = this.createMapInfoQuery(packageId, mapNames, additionalObjectTypeNames);
    return this.executeMapInfoQuery(query).pipe(
      map((data) => (!CompareHelper.isEmptyObject(data) ? new MapInfo(data) : null))
    );
  }

  getMapObjects(
    objectTypeName: string,
    objectSetName: string,
    dataSourceId: string,
    packageId?: string,
    mapNames?: string[],
    additionalColumns?: QueryColumn[],
    additionalOPath?: string,
    parameters?: ParameterValue[]
  ): Observable<any> {
    const query = this.createReadMapObjectsQuery(
      objectTypeName,
      objectSetName,
      dataSourceId,
      packageId,
      mapNames,
      additionalColumns,
      additionalOPath,
      parameters
    );
    return this.executeReadMapObjectsQuery(query).pipe(map((data) => (!CompareHelper.isEmptyObject(data) ? data : [])));
  }

  getMapObjectValues(
    queryText: string,
    objectTypeName: string,
    packageId?: string,
    mapNames?: string[]
  ): Observable<MapObjectValue[]> {
    const query = this.createReadMapObjectValuesQuery(queryText, objectTypeName, packageId, mapNames);
    return this.executeReadMapObjectValuesQuery(query).pipe(
      map((data) => (!CompareHelper.isEmptyObject(data) ? (data as any as object[]) : [])),
      map((data) => data.map((obj) => new MapObjectValue(obj)))
    );
  }

  getCurrentProfile(
    frame: BasysMapFrameService,
    withSettings: boolean = false
  ): Observable<ProfileInfoWithArenaViewModel> {
    return this.profileService
      .getCurrentProfile(withSettings)
      .pipe(
        map((data) =>
          !CompareHelper.isEmptyObject(data) ? new ProfileInfoWithArenaViewModel(data, frame?.objectSpace) : null
        )
      );
  }

  private createMapInfoQuery(
    packageId?: string,
    mapNames?: string[],
    additionalObjectTypeNames?: string[]
  ): QueryMapInfoModel {
    const queryDocument: QueryMapInfoModel = new QueryMapInfoModel();
    if (packageId) {
      queryDocument.PackageId = packageId;
    }
    if (mapNames) {
      queryDocument.MapNames = mapNames;
    }
    if (this.config.configuration.ApplicationIdentifier) {
      queryDocument.AppIdentifier = this.config.configuration.ApplicationIdentifier;
    }
    if (additionalObjectTypeNames) {
      queryDocument.AdditionalObjectTypeNames = additionalObjectTypeNames;
    }
    return queryDocument;
  }

  private createReadMapObjectsQuery(
    objectTypeName: string,
    objectSetName: string,
    dataSourceId?: string,
    packageId?: string,
    mapNames?: string[],
    additionalColumns?: QueryColumn[],
    additionalOPath?: string,
    parameters?: ParameterValue[]
  ): QueryMapObjectsModel {
    const queryDocument: QueryMapObjectsModel = new QueryMapObjectsModel();
    queryDocument.ObjectTypeName = objectTypeName;
    queryDocument.ObjectSetName = objectSetName;
    queryDocument.DataSourceId = dataSourceId;
    queryDocument.AdditionalColumns = additionalColumns;

    if (packageId) {
      queryDocument.PackageId = packageId;
    }
    if (mapNames) {
      queryDocument.MapNames = mapNames;
    }
    if (additionalOPath) {
      queryDocument.AdditionalOPath = additionalOPath;
    }
    if (parameters) {
      queryDocument.Parameters = parameters;
    }
    return queryDocument;
  }

  public createQueryColumn(oPath: string, name?: string): QueryColumn {
    return this.coreDataService.createQueryColumn(oPath, name);
  }

  private createReadMapObjectValuesQuery(
    queryText: string,
    objectTypeName: string,
    packageId?: string,
    mapNames?: string[]
  ): QueryMapObjectValuesModel {
    const queryDocument: QueryMapObjectValuesModel = new QueryMapObjectValuesModel();
    queryDocument.Query = queryText;
    queryDocument.ObjectTypeName = objectTypeName;

    if (packageId) {
      queryDocument.PackageId = packageId;
    }
    if (mapNames) {
      queryDocument.MapNames = mapNames;
    }
    return queryDocument;
  }

  private executeMapInfoQuery(query: QueryMapInfoModel): Observable<ArrayBuffer> {
    return this.coreDataService.executeWebApiPOSTMethod(this.config.configuration.WebApi.ServiceURLs.MapInfo, query);
  }
  private executeReadMapObjectsQuery(query: QueryMapObjectsModel): Observable<ArrayBuffer> {
    return this.coreDataService.executeWebApiPOSTMethod(
      this.config.configuration.WebApi.ServiceURLs.ReadMapObjects,
      query
    );
  }
  private executeReadMapObjectValuesQuery(query: QueryMapObjectsModel): Observable<ArrayBuffer> {
    return this.coreDataService.executeWebApiPOSTMethod(
      this.config.configuration.WebApi.ServiceURLs.ReadMapObjectValues,
      query
    );
  }
  private executeArenaReadCurrentSettingsQuery(query: ModelBase): Observable<ArrayBuffer> {
    return this.coreDataService.executeWebApiPOSTMethod(
      this.config.configuration.WebApi.ServiceURLs.ArenaReadCurrentSettings,
      query
    );
  }

  public setMapInfo(
    mapInfo: MapInfoViewModel,
    queryMapObjects: (ot: MapInfoObjectTypeViewModel, ctx: CacheContext) => Observable<any>
  ): void {
    let index = -1;
    if (this.cacheDataSet) {
      index = this.cache.storage.items.indexOf(this.cacheDataSet);
    }

    this.cacheDataSet = new CacheDataSet('MapObjects');

    if (mapInfo?.objectTypes.length) {
      this.cacheDataSet.cacheMethod = (ctx) =>
        this.downloadMapObjects(ctx, this.cacheDataSet, mapInfo, queryMapObjects);
    }

    if (index >= 0) {
      this.cache.storage.items[index] = this.cacheDataSet;
    } else {
      this.cache.storage.items.push(this.cacheDataSet);
    }
  }

  private downloadMapObjects(
    ctx: CacheContext,
    item: CacheDataSet,
    mapInfo: MapInfoViewModel,
    queryMapObjects: (ot: MapInfoObjectTypeViewModel, ctx: CacheContext) => Observable<any>
  ): Observable<CacheProgress> {
    const progress = new Subject<CacheProgress>();

    let errorCount = 0;
    let stepCount = 0;
    const maxCount = mapInfo.objectTypes.length;

    ctx.storage.createStorageDataSet(item, {}).then((_) => {
      progress.next(new CacheProgress(0));
      of(mapInfo.objectTypes)
        .pipe(
          concatMap((objectTypes) => {
            const observables: Observable<void>[] = [];
            for (let ot of objectTypes) {
              observables.push(
                queryMapObjects(ot, ctx).pipe(
                  concatMap((data) => {
                    return from(_.set(ot.objectSetName ?? ot.name, data)).pipe(
                      tap(() => {
                        progress.next(new CacheProgress(++stepCount / maxCount));
                      })
                    );
                  })
                )
              );
            }
            return forkJoin(observables);
          })
        )
        .subscribe(
          () => {},
          (err) => {
            progress.next(
              new CacheProgress(
                stepCount / maxCount,
                ++errorCount / maxCount,
                this.cache.error.getFullErrorMessage(err)
              )
            );
          }
        );
    });

    return CacheProgress.toObservable(progress);
  }

  public async tryLoadMapObjects(objectType: MapInfoObjectTypeViewModel, filter?: string): Promise<any> {
    const storage = await this.cache.storage.getStorageDataSet(this.cacheDataSet);
    return await storage?.get(objectType.objectSetName ?? objectType.name);
  }

  public async queryDesignatedEntityForMovingNode(entity: DataEntity): Promise<number[]> {
    let retCoordinate: number[] = [];
    const query = this.createEntityGeometryNodeQueryWw(entity); //now only applied to WasteWater
    const entityGeometry = await this.coreDataService
      .executeReadObjectsQuery(query)
      .pipe(
        map((results) => {
          return this.getResultObject<EntityGeometry>(results);
        })
      )
      .toPromise();

    if (entityGeometry) {
      const identifiersWw = ['SMP', 'KOP', 'AP']; //Valid node identifiers for Ww
      const points = entityGeometry.geometry__Json?.Points;

      let foundPoint: any;
      if (points) {
        for (let idf of identifiersWw) {
          foundPoint = points.find((p) => p.identifier.toUpperCase() === idf);
          if (foundPoint) {
            break;
          }
        }
      }

      if (foundPoint) {
        const coordinate = foundPoint.Feature.Coordinate;
        const x = coordinate.x;
        const y = coordinate.y;
        const z = coordinate.z;
        retCoordinate = [x, y, z];
      }
    }

    return retCoordinate;
  }

  private createEntityGeometryNodeQueryWw(nodeEntity: DataEntity): QueryObjectsModel {
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    const query: Query = new Query();
    query.ObjectType = 'AbwKnoten';
    query.OPath = "Id = '" + nodeEntity.id + "' And ParentId = $CurrentMunicipalityVariantId";

    const colums: QueryColumn[] = [];

    colums.push({ Name: 'id', OPath: 'Id' });
    colums.push({ Name: 'objectType', OPath: 'ObjectType' });
    colums.push({ Name: 'geometry__Json', OPath: 'Geometry__Json' });

    query.Columns = colums;
    queryDocument.ObjectSpaceName = 'BaSYS.Complete';
    queryDocument.OutputMode = QueryOutputMode.Json;
    queryDocument.ObjectQueries = [query];

    return queryDocument;
  }

  private getResultsArray<T>(results: ArrayBuffer): T[] {
    if (results && Array.isArray(results)) {
      return results;
    } else {
      return [];
    }
  }

  private getResultObject<T>(results: ArrayBuffer): T | undefined {
    const arr = this.getResultsArray<T>(results);
    if (arr?.length) {
      return arr[0];
    }
    return undefined;
  }
  public saveCoordinates(objectId: string, objectType: string, geometryAsJson: any): Observable<any> {
    const updateCoordinatesQuery: PersistObjectsModel = {
      OutputMode: 0,
      Parameters: [],
      Objects: [
        [
          {
            ObjectKey: {
              Id: objectId,
              ObjectType: objectType,
            },
            Mode: 1,
            TargetColumns: [
              {
                Name: 'Geometry__Json',
                Value: geometryAsJson,
              },
            ],
          },
        ],
      ],
    };

    console.log('Sending Update Request for Coordinates:', JSON.stringify(updateCoordinatesQuery, null, 2));
    return this.coreDataService.executePersistObjectsQuery(updateCoordinatesQuery);
  }

  private createExistingGeoQuery(): QueryObjectsModel {
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    const query: Query = new Query();
    query.ObjectType = 'MtTask';
    // query.OPath = "Id = '" + this.objectId + "'";

    const colums: QueryColumn[] = [];

    colums.push({ Name: 'Id', OPath: 'ParentId' });
    colums.push({ Name: 'ObjectType', OPath: 'TypeName()' });

    query.Columns = colums;
    queryDocument.ObjectSpaceName = 'BaSYS.Complete';
    queryDocument.OutputMode = QueryOutputMode.Json;
    queryDocument.ObjectQueries = [query];

    return queryDocument;
  }
}
